import PageHeader from "components/PageHeader/PageHeader";
import { FC, useEffect, useMemo, useState } from "react";
import SectionGridFilterCard from "./SectionGridFilterCard";
import { Helmet } from "react-helmet-async";
import { getCategories } from "data/api";
import { API_BASE_URL } from "data/axiosClient";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ProductDataType } from "data/types";
import { CategoryGroup } from "./ProductsCategorySidebar";

export interface ListingCarPageProps {
  className?: string;
}

const ITEMS_PER_PAGE = 12;

const ListingProductPage: FC<ListingCarPageProps> = ({ className = "" }) => {
  const { id } = useParams();
  const [productData, setProductData] = useState<ProductDataType[]>([]);
  const [categories, setCategories] = useState<CategoryGroup[]>([]);
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("search");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getCategories().then((items) => {
      setCategories(
        items.map((item: any) => ({
          id: item.id,
          title: item.title,
          items: item.items || [],
        }))
      );
    });
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [id, searchQuery]);

  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoading(true);
      setError(null);
      try {
        if (searchQuery) {
          const response = await axios.get(
            `${API_BASE_URL}/products/search/?q=${searchQuery}`
          );
          const transformedData = response.data.data.map((item: any) => ({
            ...item,
            image_urls:
              item.images?.map((img: { url: string }) => img.url) || [],
            subcategory: { id: 0, name: item.subcategory || "" },
            description: item.description || "",
            video_url: item.video_url || "",
            finishes: item.finishes || "",
            colors: item.colors || "",
            specifications: item.specifications || [],
            status: item.status || "",
          }));
          const start = (currentPage - 1) * ITEMS_PER_PAGE;
          setProductData(
            transformedData.slice(start, start + ITEMS_PER_PAGE)
          );
          setTotalCount(transformedData.length);
          setTotalPages(Math.ceil(transformedData.length / ITEMS_PER_PAGE));
        } else if (id) {
          const response = await axios.get(
            `${API_BASE_URL}/products/products-details/?subcategory=${id}&page=${currentPage}&page_size=${ITEMS_PER_PAGE}`
          );
          setProductData(response.data.results.data);
          setTotalCount(response.data.count);
          setTotalPages(Math.ceil(response.data.count / ITEMS_PER_PAGE));
        } else {
          const response = await axios.get(
            `${API_BASE_URL}/products/products-details/?page=${currentPage}&page_size=${ITEMS_PER_PAGE}`
          );
          setProductData(response.data.results.data);
          setTotalCount(response.data.count);
          setTotalPages(Math.ceil(response.data.count / ITEMS_PER_PAGE));
        }
      } catch (err) {
        console.error("Error fetching products:", err);
        setError("Failed to load products. Please try again later.");
        setProductData([]);
        setTotalCount(0);
        setTotalPages(1);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [id, searchQuery, currentPage]);

  const activeSubcategoryName = useMemo(() => {
    if (!id) return undefined;
    for (const group of categories) {
      const match = group.items.find(
        (sub) => String(sub.id) === String(id)
      );
      if (match) return match.name;
    }
    return undefined;
  }, [categories, id]);

  const pageTitle = searchQuery
    ? `Search: ${searchQuery}`
    : activeSubcategoryName || "Our Products";

  const pageSubtitle = searchQuery
    ? `Showing products matching "${searchQuery}"`
    : activeSubcategoryName
    ? `Browse ${activeSubcategoryName} profiles — aluminium, stainless steel & custom finishes available.`
    : "Browse our complete range of premium building solutions — expansion joint covers, tile trims, LED profiles, and more.";

  if (error) {
    return (
      <div className="container py-24 lg:py-32">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="bcr-btn-primary text-sm"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`nc-ListingCarPage relative overflow-hidden ${className}`}
      data-nc-id="ListingCarPage"
    >
      <Helmet>
        <title>
          {activeSubcategoryName
            ? `${activeSubcategoryName} | BCR LTD Products`
            : "BCR LTD - Products"}
        </title>
        <meta
          name="description"
          content="Explore BCR Ltd's complete product range including Expansion Joint Covers, Luxury Metal Tile Trims, Shadow Gap Profiles, LED Profiles, Skirtings, Tactile Markers, and advanced building chemicals. Available across the UAE and GCC."
        />
      </Helmet>

      <PageHeader title={pageTitle} subtitle={pageSubtitle} />

      <div className="container relative px-4 sm:px-6 pb-16 sm:pb-20 pt-6 sm:pt-8">
        <SectionGridFilterCard
          data={productData}
          categories={categories}
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          activeSubcategoryName={activeSubcategoryName}
          searchQuery={searchQuery}
          onPageChange={setCurrentPage}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

export default ListingProductPage;
